import React, { useState, useEffect } from 'react';
import { redemptionsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ShoppingBag, Clock, CheckCircle, XCircle, Truck, Star } from 'lucide-react';

const STATUS_CONFIG = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  EN_PROCESO: { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Truck },
  ENTREGADO: { label: 'Entregado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  CANCELADO: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function RedemptionsPage() {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRedemption, setSelectedRedemption] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    loadRedemptions();
  }, [filterStatus]);

  const loadRedemptions = async () => {
    try {
      const res = await redemptionsAPI.getAll(filterStatus === 'all' ? null : filterStatus);
      setRedemptions(res.data);
    } catch (error) {
      toast.error('Error al cargar canjes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await redemptionsAPI.updateStatus(selectedRedemption.id, newStatus, adminNotes);
      toast.success('Estado actualizado');
      setShowDialog(false);
      setSelectedRedemption(null);
      setNewStatus('');
      setAdminNotes('');
      loadRedemptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const openStatusDialog = (redemption) => {
    setSelectedRedemption(redemption);
    setNewStatus(redemption.status);
    setAdminNotes(redemption.admin_notes || '');
    setShowDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-EC', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="redemptions-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Canjes</h1>
          <p className="text-slate-600">Gestiona los canjes de beneficios</p>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="PENDIENTE">Pendiente</SelectItem>
            <SelectItem value="EN_PROCESO">En Proceso</SelectItem>
            <SelectItem value="ENTREGADO">Entregado</SelectItem>
            <SelectItem value="CANCELADO">Cancelado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const count = redemptions.filter(r => r.status === status).length;
          const Icon = config.icon;
          return (
            <Card key={status}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-slate-600">{config.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Redemptions List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Canjes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {redemptions.map(redemption => {
              const statusConfig = STATUS_CONFIG[redemption.status];
              const StatusIcon = statusConfig?.icon || Clock;
              return (
                <div
                  key={redemption.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                  data-testid={`redemption-${redemption.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{redemption.benefit_name}</p>
                      <p className="text-sm text-slate-600">{redemption.provider_company_name}</p>
                      <p className="text-xs text-slate-400">{formatDate(redemption.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-amber-600 font-semibold">
                        <Star className="w-4 h-4 fill-current" />
                        {redemption.points_spent} pts
                      </div>
                    </div>
                    <Badge className={statusConfig?.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig?.label}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openStatusDialog(redemption)}
                      data-testid={`update-status-${redemption.id}`}
                    >
                      Actualizar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {redemptions.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay canjes registrados</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Status Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actualizar Estado del Canje</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="text-sm text-slate-600 mb-2">
                <strong>Beneficio:</strong> {selectedRedemption?.benefit_name}
              </p>
              <p className="text-sm text-slate-600 mb-4">
                <strong>Proveedor:</strong> {selectedRedemption?.provider_company_name}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Nuevo Estado</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                  <SelectItem value="EN_PROCESO">En Proceso</SelectItem>
                  <SelectItem value="ENTREGADO">Entregado</SelectItem>
                  <SelectItem value="CANCELADO">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notas del Admin</label>
              <Input
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notas adicionales..."
              />
            </div>
            <Button onClick={handleUpdateStatus} className="w-full" data-testid="confirm-status-update">
              Actualizar Estado
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
